import * as React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios';
import {
  includes,
  mapValues,
  values,
} from 'lodash';
import PropTypes from 'prop-types';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import {
  alpha,
  useColorScheme,
} from '@mui/material/styles';

import {
  AuthService,
  TenantService,
} from '@pingum/api-services';
import { addTenant as addTenantAction } from '@pingum/app-state-management/tenant/actions';

import BillingAndPricingFaq from '../../../pages/settings/AdminSettingsPage/components/FAQCard/components/BillingAndPricingFaq';
import { primary } from '../../../utils/brandColors';
import {
  getQuestriPrimaryButtonSx,
  questriOrange,
} from '../../../utils/questriButtonStyles';
import TenantAvatar from '../../Tenant/TenantAvatar';
import BarnLogoEditor from './BarnLogoEditor';
import TrialPlanSelector from './TrialPlanSelector';

const defaultTenant = {
  displayName: '',
  name: '',
  tenantUrl: '',
  icon: null,
  iconColor: primary.main,
  stripePlan: env.stripe_barn_management_monthly_plan,
  stripeFreeTrial: true,
  stripeAddCardToken: null,
  userLimit: 0,
  application: 'questri',
};

const defaultValidation = {
  hasChanged: false,
  name: {
    isValid: true,
    message: '',
  },
  displayName: {
    isValid: true,
    message: '',
  },
};
const getTenantUrl = (name) => (name.length > 0
  ? TenantService.getTenantUrl(name)
  : '');

const noReferralSource = 'Tell us how you heard about Questri!';
const referralSources = [
  {
    label: 'Search engine',
    value: 'Search Engine',
  },
  {
    label: 'App stores',
    value: 'App Store / Google Play',
  },
  {
    label: 'AI tools',
    value: 'AI Tools',
  },
  {
    label: 'Instagram',
    value: 'Instagram',
  },
  {
    label: 'Friend or colleague',
    value: 'From a friend or colleague',
  },
  {
    label: 'Other',
    value: 'Other',
  },
];

const CreateOrganizationComponent = ({ onCancel }) => {
  const dispatch = useDispatch();
  const {
    mode,
    systemMode,
  } = useColorScheme();
  const isDark = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

  // redux state
  const { isAddingTenant: isLoading } = useSelector((state) => state.tenant);

  // local state
  const [loading, setLoading] = React.useState(false);
  const [logoProcessing, setLogoProcessing] = React.useState(false);
  const [nameTakenError, setNameTakenError] = React.useState(null);
  const [validation, setValidation] = React.useState(defaultValidation);
  const [tenant, setTenant] = React.useState(defaultTenant);
  const [howDidYouHear, setHowDidYouHear] = React.useState(noReferralSource);
  const [howDidYouHearOther, setHowDidYouHearOther] = React.useState('');

  const isFetching = isLoading || loading;

  React.useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });
  }, []);

  const addTenant = async () => {
    if (!(includes(values(mapValues(validation, (val) => {
      if (val instanceof Object) {
        return val.isValid;
      }
      return val;
    })), false))) {
      setLoading(true);
      // First call api to create tenant
      dispatch(addTenantAction(tenant)).then((newTenant) => {
        // place a small timeout on this request so we don't get unauthorized errrors
        setTimeout(() => {
          // now add the Questri barn
          const instance = axios.create({
            baseURL: `${env.REACT_APP_HORSE_API}/barns`,
            headers: {
              'content-type': 'application/json',
              'X-Tenant': newTenant.name,
              'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
              authorization: `Bearer ${AuthService.getToken()}`,
            },
          });
          instance.post('/', {
            stripePlan: tenant.stripePlan,
            displayName: newTenant.displayName,
            signUpPlatform: 'web',
            howDidYouHear: howDidYouHear === 'Other'
              ? (howDidYouHearOther || 'Other - not specified')
              : howDidYouHear,
            numberOfHorses: 10,
          }).then(() => {
            window.location.href = `${window.location.protocol}//${newTenant.name}.${window.location.host}/onboarding`;
            setLoading(false);
          }).catch(() => {
            setLoading(false);
            setNameTakenError(
              <>
                We couldn&#39;t finish creating your Questri Barn. Email{' '}
                <Link href="mailto:support@questri.ai" color="inherit">support@questri.ai</Link>{' '}
                and include your Barn name so we can help.
              </>,
            );
          });
        }, 1000)
      }).catch((err) => {
        if (err && err.message && err.message.indexOf('The name provided for this tenant is already taken') >= 0) {
          setNameTakenError(`${tenant.name} is already taken as a short name.`);
        }
        setLoading(false);
      });
    }
  };

  const handleNameChange = (e) => {
    const name = TenantService.normalizeName(e.target.value);
    const tenantUrl = getTenantUrl(name);
    const isValid = TenantService.isNameValid(name);

    setTenant({
      ...tenant,
      name,
      tenantUrl,
    });
    setValidation({
      ...validation,
      hasChanged: true,
      name: {
        isValid,
        message: !isValid
          ? 'Please provide a valid name'
          : '',
      },
    });
    setNameTakenError(false); // reset
  };

  const handleDisplayNameChange = (e) => {
    const displayName = e.target.value;
    const name = TenantService.normalizeName(displayName);
    const tenantUrl = getTenantUrl(name);
    const isValid = TenantService.isDisplayNameValid(displayName);
    const isNameValid = TenantService.isNameValid(name);

    setTenant({
      ...tenant,
      displayName,
      name,
      tenantUrl,
    });
    setValidation({
      ...validation,
      hasChanged: true,
      name: {
        isValid: isNameValid,
        message: !isNameValid
          ? 'Please provide a valid name'
          : '',
      },
      displayName: {
        isValid,
        message: !isValid
          ? 'Please provide a valid display name'
          : '',
      },
    });
    setNameTakenError(false); // reset
  };

  const handleAvatarChange = (icon) => {
    setTenant((current) => ({
      ...current,
      icon,
      avatar: icon,
    }));
  }

  const handlePlanChange = React.useCallback((stripePlan) => {
    setTenant((current) => ({
      ...current,
      stripePlan,
    }));
  }, []);

  const isValid = () => validation.hasChanged
    && !nameTakenError
    && validation.displayName.isValid
    && validation.name.isValid;

  const pageBackground = isDark
    ? '#171A18'
    : '#F4F1EA';
  const surfaceBackground = isDark
    ? '#202520'
    : '#FFFFFF';
  const accentColor = isDark
    ? '#E9AA7B'
    : '#8A4B26';
  const displayNameHelper = validation.displayName.isValid
    ? 'The name your team will see in Questri.'
    : validation.displayName.message;
  const shortNameHelper = validation.name.isValid
    ? tenant.tenantUrl || 'Used to create your unique Questri website address.'
    : validation.name.message;
  const shortNameError = !validation.name.isValid && !!validation.name.message;
  const canCreate = isValid() && !!tenant.displayName.trim() && !!tenant.name;
  const previewTenant = {
    ...tenant,
    displayName: tenant.displayName.trim() || 'Your Barn name',
    tenantUrl: tenant.tenantUrl || 'Your Barn address',
  };

  return (
    <Box
      sx={{
        backgroundColor: pageBackground,
        minHeight: 'calc(100vh - 48px)',
        mb: -6,
        px: 2,
        py: 4,
        '& p': { letterSpacing: 0 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1040,
          mx: 'auto',
        }}
      >
        <Button onClick={onCancel} disabled={isFetching} startIcon={<ArrowBackRounded />} sx={{ mb: 3 }}>
          Back to your barns
        </Button>
        <Box component="header" sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{
              color: accentColor,
              fontWeight: 600,
              letterSpacing: '0.12em',
            }}
          >
            Get started with Questri
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: 30,
                sm: 40,
              },
              fontWeight: 600,
              letterSpacing: '-0.04em',
              mb: 1,
            }}
          >
            Create your Questri Barn
          </Typography>
          <Typography color="text.secondary">
            Bring your horses, people, and daily plan together.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mt: 2,
            }}
          >
            <Chip icon={<CheckRounded />} label="14-day free trial" color="success" size="small" />
            <Typography variant="body2" color="text.secondary">No credit card required</Typography>
          </Box>
        </Box>
        <Card
          variant="outlined"
          sx={{
            p: {
              xs: 2.5,
              sm: 4,
            },
            backgroundColor: surfaceBackground,
            borderRadius: 2,
            boxShadow: 'none',
            '& .MuiInputBase-input': { fontSize: 16 },
            '& .MuiButton-root': { minHeight: 44 },
          }}
        >
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>Barn details</Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                required
                error={!validation.displayName.isValid}
                fullWidth
                variant="outlined"
                id="org-name"
                label="Questri Barn Name"
                value={tenant.displayName}
                disabled={isFetching}
                onChange={handleDisplayNameChange}
                helperText={displayNameHelper}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 8,
              }}
            >
              <TextField
                required
                error={shortNameError}
                fullWidth
                variant="outlined"
                id="short-name"
                label="Questri Barn Short Name"
                value={tenant.name}
                disabled={isFetching}
                onChange={handleNameChange}
                helperText={shortNameHelper}
              />
            </Grid>
          </Grid>
          <BarnLogoEditor
            onChange={handleAvatarChange}
            onProcessingChange={setLogoProcessing}
            disabled={isFetching}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Your Barn preview</Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mt: 1,
                }}
              >
                <TenantAvatar tenant={previewTenant} />
                <Box
                  sx={{
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                  }}
                >
                  <Typography>{previewTenant.displayName}</Typography>
                  <Typography variant="body2" color="text.secondary">{previewTenant.tenantUrl}</Typography>
                </Box>
              </Box>
            </Box>
          </BarnLogoEditor>
          <Divider sx={{ my: 3 }} />
          <TrialPlanSelector
            value={tenant.stripePlan}
            disabled={isFetching}
            onChange={handlePlanChange}
          />
          <Divider sx={{ my: 3 }} />
          <FormControl component="fieldset" fullWidth disabled={isFetching}>
            <FormLabel
              component="legend"
              sx={{
                color: 'inherit',
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              How did you hear about Questri?{' '}
              <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography>
            </FormLabel>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {referralSources.map((source) => {
                const selected = howDidYouHear === source.value;
                const nextValue = selected
                  ? noReferralSource
                  : source.value;

                return (
                  <ToggleButton
                    key={source.value}
                    value={source.value}
                    selected={selected}
                    disabled={isFetching}
                    onChange={() => setHowDidYouHear(nextValue)}
                    sx={{
                      minHeight: 44,
                      px: 2,
                      py: 1,
                      borderRadius: 6,
                      '&.Mui-selected:not(.Mui-disabled)': {
                        color: 'inherit',
                        borderColor: questriOrange.main,
                        backgroundColor: alpha(questriOrange.main, 0.12),
                        '&:hover': { backgroundColor: alpha(questriOrange.main, 0.18) },
                      },
                    }}
                  >
                    {source.label}
                  </ToggleButton>
                );
              })}
            </Box>
            {howDidYouHear === 'Other' && (
              <TextField
                sx={{
                  mt: 2,
                  maxWidth: 540,
                }}
                label="Where did you find us?"
                fullWidth
                value={howDidYouHearOther}
                disabled={isFetching}
                onChange={(event) => setHowDidYouHearOther(event.target.value)}
              />
            )}
          </FormControl>
          {!!nameTakenError && <Alert severity="error" sx={{ mt: 2 }}>{nameTakenError}</Alert>}
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
              justifyContent: 'space-between',
              gap: 2,
              mt: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
              No payment is needed today. To keep access, add a payment method before your trial ends.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column-reverse',
                  sm: 'row',
                },
                gap: 1,
              }}
            >
              <Button onClick={onCancel} disabled={isFetching}>Cancel</Button>
              <Button
                onClick={addTenant}
                variant="contained"
                disabled={!canCreate || isFetching || logoProcessing}
                endIcon={<ArrowForwardRounded />}
                sx={[
                  getQuestriPrimaryButtonSx(),
                  {
                    minWidth: 220,
                    flexShrink: 0,
                    '&.Mui-disabled': { opacity: 0.45 },
                  },
                ]}
              >
                Create Questri Barn
              </Button>
            </Box>
          </Box>
          {isFetching && (
            <Box role="status" aria-live="polite" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Setting up your Questri Barn...</Typography>
              <LinearProgress />
            </Box>
          )}
        </Card>
        <Box
          sx={{
            mt: 4,
            borderTop: '1px solid',
            borderColor: alpha(questriOrange.main, 0.2),
            pt: 3,
          }}
        >
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>Questions before you start?</Typography>
          <BillingAndPricingFaq includeOrgFaq />
        </Box>
      </Box>
    </Box>
  );
};

CreateOrganizationComponent.propTypes = { onCancel: PropTypes.func };

CreateOrganizationComponent.defaultProps = { onCancel: () => { } };

export default CreateOrganizationComponent;
